import {PackageType} from '@sollapay/enums';
import {useEffect, useState} from 'react';

import {useTrustTypeStrategy} from '@/features/trust-accounts/providers';
import {INITIAL_FORM_STATE, type FormStateSnapshot, type StepStatus} from '@/hooks';

import {ACTIVATION_PACKAGE_TABS_METADATA, getStepKeysForTab} from '../config';
import {navigateAfterSave} from '../utils';

import type {TrustTypeStrategy} from '@sollapay/domain/trust-types';

export type {FormStateSnapshot};

const buildInitialStepByTab = (strategy: TrustTypeStrategy): Record<PackageType, string> =>
  Object.fromEntries(
    Object.keys(ACTIVATION_PACKAGE_TABS_METADATA).map(tab => [
      tab,
      getStepKeysForTab(tab as PackageType, strategy)[0] ?? ''
    ])
  ) as Record<PackageType, string>;

function resolveNavState(activeTab: PackageType, activeStep: string, strategy: TrustTypeStrategy) {
  const stepsForActiveTab = getStepKeysForTab(activeTab, strategy);
  const hasSteps = stepsForActiveTab.length > 0;
  const currentStepIndex = hasSteps ? stepsForActiveTab.indexOf(activeStep) : -1;

  const isLastStep = !hasSteps || currentStepIndex === stepsForActiveTab.length - 1;
  const isLastTab = ENABLED_TAB_KEYS.indexOf(activeTab) === ENABLED_TAB_KEYS.length - 1;

  const isFirstStep = !hasSteps || currentStepIndex === 0;
  const isFirstTab = ENABLED_TAB_KEYS.indexOf(activeTab) === 0;

  return {
    stepsForActiveTab,
    hasSteps,
    currentStepIndex,
    isLastStep,
    isLastTab,
    isLastStepOfLastTab: isLastStep && isLastTab,
    isFirstStep,
    isFirstTab,
    isFirstStepOfFirstTab: isFirstStep && isFirstTab
  };
}

const TAB_KEYS = Object.keys(ACTIVATION_PACKAGE_TABS_METADATA) as PackageType[];
const ENABLED_TAB_KEYS = TAB_KEYS.filter(key => !ACTIVATION_PACKAGE_TABS_METADATA[key].comingSoon);
const TABS_DATA = Object.entries(ACTIVATION_PACKAGE_TABS_METADATA);

const toEnabledTab = (tab: PackageType): PackageType =>
  ENABLED_TAB_KEYS.includes(tab) ? tab : (ENABLED_TAB_KEYS[0] ?? tab);

export const useActivationPackageFormModal = (initialTab?: PackageType, initialStep?: string) => {
  const strategy = useTrustTypeStrategy();
  const [activeTab, setActiveTab] = useState<PackageType>(
    toEnabledTab(initialTab ?? PackageType.TRUST_DETAILS)
  );
  const [stepByTab, setStepByTab] = useState<Record<PackageType, string>>(() =>
    buildInitialStepByTab(strategy)
  );
  const [formState, setFormState] = useState<FormStateSnapshot>(INITIAL_FORM_STATE);
  const [statusByStep, setStatusByStep] = useState<Record<string, StepStatus>>({});

  const activeStep = stepByTab[activeTab];

  const {
    stepsForActiveTab,
    hasSteps,
    currentStepIndex,
    isLastStep,
    isLastStepOfLastTab,
    isFirstStep,
    isFirstStepOfFirstTab
  } = resolveNavState(activeTab, activeStep, strategy);

  useEffect(() => {
    if (initialTab) {
      const tab = toEnabledTab(initialTab);
      setActiveTab(tab);
      setFormState(INITIAL_FORM_STATE);
      if (initialStep) {
        setStepByTab(prev => ({...prev, [tab]: initialStep}));
      }
    }
  }, [initialTab, initialStep]);

  const goToTab = (tab: PackageType) => {
    setActiveTab(tab);
    setFormState(INITIAL_FORM_STATE);
  };

  const goToStep = (step: string) => {
    setStepByTab(prev => ({...prev, [activeTab]: step}));
    setFormState(INITIAL_FORM_STATE);
  };

  const handleTabChange = (tab: PackageType) => navigateAfterSave(formState, () => goToTab(tab));

  const handleStepChange = (step: string) => {
    if (step === activeStep) return;
    return navigateAfterSave(formState, () => goToStep(step));
  };

  const handleNextClick = () =>
    navigateAfterSave(formState, () => {
      if (hasSteps && !isLastStep) {
        const nextStep = stepsForActiveTab[currentStepIndex + 1];
        if (nextStep) goToStep(nextStep);
      } else {
        const nextTab = ENABLED_TAB_KEYS[ENABLED_TAB_KEYS.indexOf(activeTab) + 1];
        if (nextTab) goToTab(nextTab);
      }
    });

  const handleBackClick = () =>
    navigateAfterSave(formState, () => {
      if (hasSteps && !isFirstStep) {
        const prevStep = stepsForActiveTab[currentStepIndex - 1];
        if (prevStep) goToStep(prevStep);
      } else {
        const prevTab = ENABLED_TAB_KEYS[ENABLED_TAB_KEYS.indexOf(activeTab) - 1];
        if (prevTab) {
          const prevTabSteps = getStepKeysForTab(prevTab, strategy);
          const lastStepOfPrevTab = prevTabSteps[prevTabSteps.length - 1];
          goToTab(prevTab);
          if (lastStepOfPrevTab) {
            setStepByTab(prev => ({...prev, [prevTab]: lastStepOfPrevTab}));
          }
        }
      }
    });

  const tabsData = TABS_DATA.map(([key, {icon, labelKey, comingSoon}]) => ({
    value: key as PackageType,
    icon,
    labelKey,
    comingSoon
  }));

  // Copies only the two flags — the full snapshot also carries save/cancel closures.
  const handleStepStatus = (step: string, {hasErrors, isLocked}: StepStatus) => {
    setStatusByStep(prev => ({...prev, [step]: {hasErrors, isLocked}}));
  };

  const handleFormStateChange = (state: FormStateSnapshot) => {
    setFormState(state);
    handleStepStatus(activeStep, state);
  };

  const navigateTo = (tab: PackageType, step?: string) =>
    navigateAfterSave(formState, () => {
      goToTab(tab);
      if (step) {
        setStepByTab(prev => ({...prev, [tab]: step}));
      }
    });

  return {
    tabsData,
    activeTab,
    activeStep,
    formState,
    statusByStep,
    onFormStateChange: handleFormStateChange,
    onStepStatus: handleStepStatus,
    handleTabChange,
    handleStepChange,
    handleNextClick,
    handleBackClick,
    navigateTo,
    isLastStepOfLastTab,
    isFirstStepOfFirstTab
  };
};
